import React, { useMemo, useState } from 'react';
import { Alert, Button, Col, Divider, Modal, Row, Space, Typography } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import { AxiosResponse } from 'axios';
import useFetch from '../../../../../hooks/useFetch';
import { FetchApiInterface } from '../../../../../interfaces/FetchApi.interface';

const { Text } = Typography;

interface EnvioTraduccionesProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedRows: any[];
  onConfirm: () => void;
}

const SENDABLE_STATUSES = ['TRANSLATED', 'OBSERVED'];

interface ResumenPorSolicitante {
  solicitante: string;
  total: number;
  conObservaciones: number;
  sinObservaciones: number;
}

const EnvioTraducciones: React.FC<EnvioTraduccionesProps> = ({
  open,
  setOpen,
  selectedRows,
  onConfirm,
}) => {
  const { fetchApi } = useFetch();
  const [sending, setSending] = useState(false);

  const validRows   = useMemo(() => selectedRows.filter(r => SENDABLE_STATUSES.includes(r.communicationStatus)), [selectedRows]);
  const invalidRows = useMemo(() => selectedRows.filter(r => !SENDABLE_STATUSES.includes(r.communicationStatus)), [selectedRows]);

  const porSolicitante = useMemo<ResumenPorSolicitante[]>(() => {
    const map = new Map<string, ResumenPorSolicitante>();
    validRows.forEach(row => {
      const solicitante = `${row.userRequestName ?? ''} ${row.userRequestLastname ?? ''}`.trim() || 'Sin solicitante';
      if (!map.has(solicitante)) {
        map.set(solicitante, { solicitante, total: 0, conObservaciones: 0, sinObservaciones: 0 });
      }
      const entry = map.get(solicitante)!;
      entry.total += 1;
      if (row.communicationStatus === 'OBSERVED') {
        entry.conObservaciones += 1;
      } else {
        entry.sinObservaciones += 1;
      }
    });
    return Array.from(map.values());
  }, [validRows]);

  const handleConfirm = () => {
    const payload = { requestIds: validRows.map((r: any) => r.id) };
    setSending(true);
    fetchApi({ url: '/translations/requests/send', method: 'PATCH', data: payload } as FetchApiInterface)
      .then(({ data }: AxiosResponse<any>) => {
        if (Array.isArray(data) || data?.status === 'success') {
          onConfirm();
        }
      })
      .catch(() => {})
      .finally(() => setSending(false));
  };

  return (
    <>
      <Button
        icon={<SendOutlined />}
        disabled={selectedRows.length === 0}
        onClick={() => setOpen(true)}
      >
        Envío traducciones
      </Button>

      <Modal
        centered
        width={580}
        title="Envío traducciones"
        open={open}
        onCancel={() => setOpen(false)}
        footer={
          <Row justify="end">
            <Col>
              <Space>
                <Button shape="round" type="text" onClick={() => setOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  shape="round"
                  type="primary"
                  loading={sending}
                  disabled={validRows.length === 0}
                  onClick={handleConfirm}
                >
                  Aceptar
                </Button>
              </Space>
            </Col>
          </Row>
        }
      >
        <Row gutter={[0, 12]}>

          {invalidRows.length > 0 && (
            <Col span={24}>
              <Alert
                type="warning"
                showIcon
                message={`${invalidRows.length} comunicación(es) excluida(s) por no estar en estado "Traducido" u "Observado" y no serán enviadas.`}
              />
            </Col>
          )}

          {validRows.length === 0 && (
            <Col span={24}>
              <Alert
                type="error"
                showIcon
                message='Ninguna comunicación seleccionada tiene estado válido para enviar. Deben estar en "Traducido" u "Observado".'
              />
            </Col>
          )}

          {porSolicitante.map((r, i) => (
            <Col span={24} key={r.solicitante}>
              <Text strong>
                Se enviarán {r.total} traducción(es) al solicitante: {r.solicitante}
              </Text>
              <Row style={{ paddingTop: 6, paddingLeft: 8 }} gutter={[0, 2]}>
                <Col span={24}><Text>• Traducciones realizadas: <b>{r.total}</b></Text></Col>
                <Col span={24}><Text>• Con observaciones: <b>{r.conObservaciones}</b></Text></Col>
                <Col span={24}><Text>• Sin observaciones: <b>{r.sinObservaciones}</b></Text></Col>
              </Row>
              {i < porSolicitante.length - 1 && <Divider style={{ margin: '10px 0' }} />}
            </Col>
          ))}

          {validRows.length > 0 && (
            <>
              <Divider style={{ margin: '4px 0' }} />
              <Col span={24}>
                <Text strong>Total a enviar: {validRows.length} comunicación(es)</Text>
              </Col>
              <Col span={24}>
                <Text type="secondary">¿Está seguro que desea realizar esta acción?</Text>
              </Col>
            </>
          )}

        </Row>
      </Modal>
    </>
  );
};

export default EnvioTraducciones;
